import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Trash } from '@saleshandy/icons';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import toaster, { Theme } from '../../../../../../shared/toaster';
import AddToSequenceModal from '../modals/add-to-sequence-modal';
import type { IProps } from './prospect-details-container';
import ProspectDetailsModal from './prospect-details-modal';
import TagsModal from '../modals/tags-modal';
import hasResource from '../../../../../../shared/utils/access-control/has-resource';
import { ResourceIdentifiers } from '../../../../../../shared/utils/access-control/enums/resource-identifiers';
import { executeOnRequestStatus } from '../../../../../../shared/utils';
import { isFreePlan } from '../../../../../../shared/utils/plans';
import planPermissionError from '../../../../../../shared/utils/errors/plan-permission-error';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import { useProspectDetailsUrl } from '../../../../../../shared/hooks/use-prospect-details-url';
import { FilterLogic } from '../../../prospect-filters-modal/types';
import { ViewType } from '../../../../enums/prospect';
import {
  ContactStatusFilterEnum,
  SortOrder,
} from '../../../../../sequence/enums';
import ConfirmationModal from '../../../../../../shared/components/confirmation-modal';

const ProspectDetails: React.FC<IProps> = ({
  show,
  onHide,
  selectedProspectId,
  setSelectedProspectId,
  resetSelectedProspectId,
  selectedProspectData,
  selectedProspectSequences,
  addToStepRequestError,
  addToStepRequestStatus,
  resetSequenceAndStepData,
  addToStepRequestFailedCount,
  addToStepRequestSuccessfulCount,
  sendAddContactToStepRequest,
  sendUnsubscribeProspectRequest,
  sendRemoveProspectRequest,
  sendSequenceMarkAsRepliedRequest,
  unsubscribeProspectStatus,
  unsubscribeProspectError,
  removeProspectError,
  removeProspectStatus,
  unsubscribeProspectMessage,
  removeProspectMessage,
  addToStepRequestMessage,
  sendGetSingleProspectSequencesDetailsRequest,
  getSingleProspectSequencesDetailsStatus,
  markAsRepliedError,
  markAsRepliedMessage,
  markAsRepliedStatus,
  sendSingleProspectRemoveFromSequencesRequest,
  removeFromSequenceError,
  removeFromSequenceMessage,
  removeFromSequenceStatus,
  saveContactAttributesRequestStatus,
  saveContactAttributesRequestMessage,
  saveContactAttributesRequestError,
  sendSaveContactAttributesRequest,
  sendGetSingleProspectDetailsRequest,
  getSingleProspectDetailsStatus,
  sendSequenceMarkAsFinishedRequest,
  markAsFinishedStatus,
  markAsFinishedMessage,
  markAsFinishedError,
  isTagsFilterApplied,
  resetRemoveProspectRequest,
  resetSaveContactAttributesRequestStatus,
  sendPrimaryEmailChangeInProspectRequest,
  sendPrimaryPhoneChangeInProspectRequest,
  sendDeleteEmailFromProspectRequest,
  sendDeletePhoneFromProspectRequest,
  deleteSecondaryEmailRequestStatus,
  resetProspectDeleteEmailRequestStatus,
  deleteSecondaryPhoneRequestStatus,
  resetProspectDeletePhoneRequestStatus,
  primaryEmailChangeRequestStatus,
  primaryPhoneChangeRequestStatus,
  resetProspectPrimaryEmailChangeRequestStatus,
  resetUpdatePrimaryNumberState,
  allOutcomes,
  currentSequenceId,
  sendGetTagsRequest,
  sendAddTagsToProspectsRequest,
  resetTags,
  tags,
  addTagsToProspectsRequestStatus,
  fields,
  defaultTab = 'activity',
  sendGetTasksRequest,
  getTasksRequestStatus,
  tasks,
  sendUpdateTasksRequest,
  taskActionRequestStatus,
  taskActionRequestMessage,
  sendGetUndoTasksRequest,
  getUndoTasksRequestStatus,
  sendGetTaskAssigneeListRequest,
  taskAssigneeList,
  sendCreateTaskRequest,
  createTaskRequestStatus,
  createTaskRequestMessage,
  createTaskRequestError,
  sendUploadAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  sendDeleteAttachmentRequest,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  sendImageUploadRequest,
  resetUploadAttachmentRequest,
  resetDeleteAttachmentRequest,
  resetCreateTaskRequest,
  sendGetShareableTaskerDataRequest,
  getShareableTaskerDataRequestStatus,
  shareableTaskerData,
  resetShareableTaskerState,
  sendGetWhatsappLinkRequest,
  getWhatsappLinkRequestStatus,
  getWhatsappLinkResponse,
  resetGetWhatsappLinkState,
  setShowGlobalRestrictionModal,
  sendCreateProspectNoteRequest,
  createProspectNoteRequestStatus,
  createProspectNoteRequestMessage,
  createProspectNoteResponse,
  resetCreateProspectNotesListRequest,
  sendUpdateProspectNoteRequest,
  updateProspectNoteRequestStatus,
  resetUpdateProspectNoteRequest,
  getFieldsRequest,
  getSinglePropsectDetailPinnedFieldsRequest,
  updateProspectPinStatusRequest,
  sendUpdateProspectFieldByIdRequest,
  prospectToNavigateFromModal,
  getSingleProspect,
  appendProspectIdsToNavigateFromModal,
  sendGetProspectListRequest,
  sendGetContactListByViewIdRequest,
  filters,
  selectedSavedView,
  getProspectListRequestStatus,
  getContactListByViewIdRequestStatus,
  getSequenceContactListRequestStatus,
  prospects,
  prospectKanbanData,
  prospectKanbanViewData,
  selectedKanbanCategoryId,
  activeViewId,
  resetUpdateProspectFieldByIdRequest,
  setProspectToNavigateFromModal,
  sequenceProspects,
  getSequenceContactListRequest,
  getSequenceListRequest,
  getStepListRequest,
  sequenceProspectsCount,
  prospectCount,
  prospectKanbanCount,
  sendRemoveTagsFromProspectsRequest,
  removeTagsFromProspectsRequestStatus,
  sendAddTagsToProspectsRequestStatus,
  sendRemoveTagsFromProspectsRequestStatus,
  sendGetProspectListCountRequest,
  setProspectViewIdFilter,
  sendGetProspectActivityCountRequest,
  getProspectActivityCountRequestStatus,
  editProspectAcitivityCount,
  resetLoadedSkeletonForCurrrentProspect,
  resetGetSingleProspectSequencesDetailsRequest,
  resetGetTasksRequest,
  isDemoAccount,
  callLogsVariant = false,
  callLogRow = null,
  callLogsProfileHeader = null,
  prospectDetail = null,
}) => {
  const location = window?.location?.pathname;
  const {
    openProspectDetails,
    closeProspectDetails,
    tabFromUrl,
  } = useProspectDetailsUrl();

  // Extract sequence ID from URL if currentSequenceId prop is not available
  const getSequenceIdFromUrl = () => {
    if (currentSequenceId) {
      return currentSequenceId.toString();
    }
    if (location?.includes('/sequence/')) {
      const match = location?.match(/^\/sequence\/(\d+)/);
      return match ? match[1] : null;
    }
    return null;
  };
  const [showAddToSequenceModal, setShowAddToSequenceModal] = useState<boolean>(
    false,
  );

  const [showDeleteProspect, setShowDeleteProspectModal] = useState<boolean>(
    false,
  );
  const [allListingProspects, setAllListingProspects] = useState([]);
  const [showTagsModal, setShowTagsModal] = useState<boolean>(false);
  const [disabledModalNavArrows, setDisabledModalNavArrows] = useState<
    ('left' | 'right')[]
  >([]);
  const prevShowRef = useRef<boolean>(false);
  const prevProspectIdRef = useRef<number | null>(null);
  const isLoadingNextPageRef = useRef<boolean>(false);
  const currentPageRef = useRef<number>(0);

  const hideAddToSequenceStepModal = () => {
    setShowAddToSequenceModal(false);
  };

  const handleAddToSequenceStepModal = () => {
    if (isFreePlan() && !hasPermission(Permissions.SEQUENCE_PROSPECT_WRITE)) {
      const title = 'Please upgrade your plan to start adding prospects';
      const content = [
        'Your account is downgraded to a free plan. Upgrade to paid plan to add prospects and resume email sending.',
      ];

      planPermissionError.showModal(title, content, true);
      return;
    }

    if (!selectedProspectData?.isSubscribed) {
      toaster.error(
        'It is not possible to add unsubscribed contacts to the sequence',
        { theme: Theme.New },
      );
    } else if (hasResource(ResourceIdentifiers.CONTACTS_IMPORT)) {
      setShowAddToSequenceModal(true);
    }
  };

  const hideDeleteProspectModal = () => {
    setShowDeleteProspectModal(false);
  };

  const showDeleteProspectModal = () => {
    setShowDeleteProspectModal(true);
  };

  const hideTagsModal = () => {
    /**
     * Handling a case in which a tag filter is applied and the same tag is deleted from the selected
     * Prospect.
     */
    isTagsFilterApplied && onHide();
    setShowTagsModal(false);
  };

  const currentProspectIndex = useMemo(
    () => prospectToNavigateFromModal?.ids?.indexOf(selectedProspectId) ?? null,
    [prospectToNavigateFromModal, selectedProspectId],
  );

  const handleSetDisabledModalNavArrows = useCallback(() => {
    const prosectIdsLenght = prospectToNavigateFromModal?.ids?.length;

    if (prosectIdsLenght === 1) {
      setDisabledModalNavArrows(['left', 'right']);
    } else if (currentProspectIndex === 0) {
      setDisabledModalNavArrows(['left']);
    } else if (currentProspectIndex === prosectIdsLenght - 1) {
      setDisabledModalNavArrows(['right']);
    } else {
      setDisabledModalNavArrows([]);
    }
  }, [prospectToNavigateFromModal, currentProspectIndex]);

  const fetchNextPageProspects = useCallback(async () => {
    if (
      isLoadingNextPageRef.current ||
      !filters ||
      !prospectToNavigateFromModal?.columnName ||
      disabledModalNavArrows?.length === 2
    ) {
      return;
    }

    isLoadingNextPageRef.current = true;

    const sequenceId = getSequenceIdFromUrl();
    const isSequenceView = sequenceId && location?.includes('/sequence/');

    // For sequence view, pages start from 0, so we use currentPageRef directly
    const nextPage = isSequenceView
      ? currentPageRef.current
      : currentPageRef.current + 1;

    try {
      const {
        pageSize,
        selectedFields,
        sortBy,
        sortOrder,
        search,
        filterGroupLogic,
      } = filters;

      const columnName = prospectToNavigateFromModal?.columnName;
      const isUnassigned = columnName === 'Unassigned';
      const activeColumnKey = isUnassigned ? 'UnAssigned' : columnName;
      const isKanbanView =
        !!columnName &&
        (prospectKanbanCount?.[activeColumnKey] !== undefined ||
          prospectKanbanCount?.[columnName] !== undefined);

      const payload: any = {
        pageNum: nextPage,
        pageSize: pageSize || 25,
        sortBy:
          // eslint-disable-next-line no-restricted-globals
          sortBy != null && !isNaN(Number(sortBy)) ? Number(sortBy) : sortBy,
        sortOrder,
        search,
      };
      if (isSequenceView) {
        const sequencePayload: any = {
          pageNum: nextPage,
          pageSize: pageSize || 25,
          status: ContactStatusFilterEnum.Total,
          sortOrder: SortOrder.Asc,
        };
        const totalIds = prospectToNavigateFromModal?.ids?.length || 0;
        const totalSequenceProspectCount = sequenceProspectsCount || 0;

        if (totalSequenceProspectCount > totalIds) {
          getSequenceContactListRequest(sequenceId, sequencePayload);
        } else {
          isLoadingNextPageRef.current = false;
        }
        return;
      }

      if (isKanbanView) {
        // Kanban API call
        const filterLogic = filterGroupLogic || {};
        const filterGroupLogicPayload = {
          filters: (filterLogic as any)?.filters || [],
          group_logic: (filterLogic as any)?.group_logic || FilterLogic.AND,
        };

        payload.viewType = ViewType.Kanban;
        payload.activeColumns = [activeColumnKey];
        payload.kanbanViewId = selectedKanbanCategoryId ?? activeViewId;
        payload.filterGroupLogic = filterGroupLogicPayload;

        // If there's an activeViewId, use getContactListByViewIdRequest
        if (activeViewId) {
          payload.viewId = activeViewId;
          await sendGetContactListByViewIdRequest(payload);
        } else {
          await sendGetProspectListRequest(payload);
        }
      } else {
        // List view API call
        payload.selectedField =
          selectedFields?.map((f) => f.id.toString()) || [];
        if (filterGroupLogic) {
          payload.filterGroupLogic = filterGroupLogic;
        }

        // If there's a selected saved view, use getContactListByViewIdRequest
        if (selectedSavedView?.id) {
          payload.viewId = selectedSavedView.id;
          payload.viewType = ViewType.List;
          await sendGetContactListByViewIdRequest(payload);
        } else {
          await sendGetProspectListRequest(payload);
        }
      }
    } catch (error) {
      console.error('Error fetching next page prospects:', error);
      isLoadingNextPageRef.current = false;
    }
  }, [
    filters,
    prospectToNavigateFromModal?.columnName,
    selectedSavedView,
    selectedKanbanCategoryId,
    activeViewId,
    sendGetProspectListRequest,
    sendGetContactListByViewIdRequest,
    disabledModalNavArrows,
    prospectKanbanCount,
    prospectToNavigateFromModal?.ids,
    sequenceProspectsCount,
    getSequenceContactListRequest,
    location,
  ]);

  // Handle appending new prospect IDs when API response succeeds
  useEffect(() => {
    const isRequestSucceeded =
      (getProspectListRequestStatus === RequestStatus.Succeeded ||
        getContactListByViewIdRequestStatus === RequestStatus.Succeeded ||
        getSequenceContactListRequestStatus === RequestStatus.Succeeded) &&
      isLoadingNextPageRef.current;

    if (isRequestSucceeded) {
      const existingIds = prospectToNavigateFromModal?.ids || [];

      const sequenceId = getSequenceIdFromUrl();
      const isSequenceView = sequenceId && location?.includes('/sequence/');

      let newProspectIds: number[] = [];

      if (isSequenceView) {
        newProspectIds =
          sequenceProspects
            ?.map((item: any) => item?.prospect?.id || item?.id)
            .filter(
              (id: any) =>
                id !== undefined && id !== null && !existingIds.includes(id),
            ) || [];
      } else {
        // Determine if it's kanban view by checking if columnName exists in prospectKanbanCount
        const columnName = prospectToNavigateFromModal?.columnName;
        const isUnassigned = columnName === 'Unassigned';
        const activeColumnKey = isUnassigned ? 'UnAssigned' : columnName;
        const isKanbanView =
          !!columnName &&
          (prospectKanbanCount?.[activeColumnKey] !== undefined ||
            prospectKanbanCount?.[columnName] !== undefined);

        if (isKanbanView) {
          const kanbanDataSource = activeViewId
            ? prospectKanbanViewData?.contacts || {}
            : prospectKanbanData || {};
          const columnProspects = kanbanDataSource[activeColumnKey] || [];

          newProspectIds = columnProspects
            .map((prospect: any) => prospect.id)
            .filter(
              (id: any) =>
                id !== undefined && id !== null && !existingIds.includes(id),
            );
        } else {
          // For list view, extract IDs from prospects array
          newProspectIds = prospects
            .map((prospect: any) => prospect.id)
            .filter(
              (id: any) =>
                id !== undefined && id !== null && !existingIds.includes(id),
            );
        }
      }

      if (newProspectIds.length > 0) {
        appendProspectIdsToNavigateFromModal(newProspectIds);
      }

      isLoadingNextPageRef.current = false;
      currentPageRef.current += 1;
    }
  }, [
    getProspectListRequestStatus,
    getContactListByViewIdRequestStatus,
    getSequenceContactListRequestStatus,
    prospects,
    sequenceProspects,
    prospectKanbanData,
    prospectKanbanViewData,
    activeViewId,
    prospectToNavigateFromModal?.ids,
    prospectToNavigateFromModal?.columnName,
    appendProspectIdsToNavigateFromModal,
    prospectKanbanCount,
    location,
  ]);

  const handleModalNavArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      if (prospectToNavigateFromModal?.ids?.length > 0) {
        // reseting necessary states for the current prospect
        resetLoadedSkeletonForCurrrentProspect();
        resetGetSingleProspectSequencesDetailsRequest();
        resetGetTasksRequest();
        const newIndex = currentProspectIndex + (direction === 'left' ? -1 : 1);
        const totalIds = prospectToNavigateFromModal.ids.length;
        const remainingIds = totalIds - newIndex - 1;

        // Check if we're 5 prospects away from the end and going forward
        if (
          direction === 'right' &&
          remainingIds <= 5 &&
          !isLoadingNextPageRef.current &&
          filters
        ) {
          const sequenceId = getSequenceIdFromUrl();
          const isSequenceView = sequenceId && location?.includes('/sequence/');

          let totalProspectCount = 0;

          if (isSequenceView) {
            totalProspectCount = sequenceProspectsCount || 0;
          } else {
            const columnName = prospectToNavigateFromModal?.columnName;
            const isUnassigned = columnName === 'Unassigned';
            const activeColumnKey = isUnassigned ? 'UnAssigned' : columnName;
            const isKanbanView =
              !!columnName &&
              (prospectKanbanCount?.[activeColumnKey] !== undefined ||
                prospectKanbanCount?.[columnName] !== undefined);

            if (isKanbanView) {
              // For kanban view, get count for the specific column
              totalProspectCount =
                prospectKanbanCount?.[activeColumnKey] ||
                prospectKanbanCount?.[columnName] ||
                0;
            } else {
              // For list view, use overall prospectCount (total across all pages)
              totalProspectCount = prospectCount || 0;
            }
          }

          if (totalProspectCount > totalIds) {
            fetchNextPageProspects();
          }
        }

        const newProspectId = prospectToNavigateFromModal?.ids?.[newIndex];
        handleSetDisabledModalNavArrows();
        if (newProspectId) {
          setSelectedProspectId?.(newProspectId);
          window.dispatchEvent(
            new CustomEvent('prospect:openProspectDetailsModal', {
              detail: {
                prospectId: newProspectId,
                tab: tabFromUrl || 'activity',
              },
            }),
          );
        }
      }
    },
    [
      prospectToNavigateFromModal,
      tabFromUrl,
      currentProspectIndex,
      handleSetDisabledModalNavArrows,
      fetchNextPageProspects,
      filters,
      setSelectedProspectId,
      prospectCount,
      prospectKanbanCount,
      sequenceProspectsCount,
      location,
    ],
  );

  // Set disabled modal nav arrows when component mounts
  useEffect(() => {
    handleSetDisabledModalNavArrows();
  }, [handleSetDisabledModalNavArrows]);

  // Initialize current page ref based on filters or prospectToNavigateFromModal
  useEffect(() => {
    const sequenceId = getSequenceIdFromUrl();
    const isSequenceView = sequenceId && location?.includes('/sequence/');

    if (filters?.pageNum !== undefined) {
      currentPageRef.current = filters.pageNum;
    } else if (
      prospectToNavigateFromModal?.ids?.length > 0 &&
      filters?.pageSize
    ) {
      if (isSequenceView) {
        const totalIds = prospectToNavigateFromModal.ids.length;
        currentPageRef.current = Math.floor(
          totalIds / (filters.pageSize || 25),
        );
      } else {
        const totalIds = prospectToNavigateFromModal.ids.length;
        currentPageRef.current =
          Math.floor((totalIds - 1) / (filters.pageSize || 25)) + 1;
      }
    } else if (isSequenceView) {
      currentPageRef.current = 0;
    }
  }, [
    prospectToNavigateFromModal?.ids?.length,
    filters?.pageSize,
    filters?.pageNum,
    location,
  ]);

  // Reset loading flag if API fails
  useEffect(() => {
    if (
      (getProspectListRequestStatus === RequestStatus.Failed ||
        getContactListByViewIdRequestStatus === RequestStatus.Failed ||
        getSequenceContactListRequestStatus === RequestStatus.Failed) &&
      isLoadingNextPageRef.current
    ) {
      isLoadingNextPageRef.current = false;
    }
  }, [
    getProspectListRequestStatus,
    getContactListByViewIdRequestStatus,
    getSequenceContactListRequestStatus,
  ]);

  // Handle URL updates when modal opens or prospectId changes (but not on tab changes)
  useEffect(() => {
    // Update URL when modal transitions from closed to open, or when prospectId changes
    if (show && selectedProspectId) {
      const isModalJustOpened = !prevShowRef.current;
      const prospectIdChanged =
        prevProspectIdRef.current !== selectedProspectId;

      if (isModalJustOpened || prospectIdChanged) {
        if (tabFromUrl) {
          prevProspectIdRef.current = selectedProspectId;
          return;
        }

        const tabToUse = defaultTab || 'activity';
        openProspectDetails(selectedProspectId, tabToUse);
        prevProspectIdRef.current = selectedProspectId;
      }
    }
    prevShowRef.current = show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedProspectId, tabFromUrl]);

  // Handle URL updates when modal closes
  const handleHide = () => {
    if (location?.includes('/crm/kanban')) {
      if (activeViewId) {
        sendGetContactListByViewIdRequest({
          viewId: activeViewId,
          viewType: ViewType.Kanban,
          filterGroupLogic: filters?.filterGroupLogic,
          pageSize: filters?.pageSize,
          pageNum: filters?.pageNum,
          sortOrder: filters?.sortOrder,
          sortBy:
            // eslint-disable-next-line no-restricted-globals
            filters?.sortBy != null && !isNaN(Number(filters?.sortBy))
              ? Number(filters.sortBy)
              : filters?.sortBy,
          search: filters?.search,
        });
      } else {
        sendGetProspectListRequest({
          viewType: ViewType.Kanban,
          filterGroupLogic: filters?.filterGroupLogic,
          pageSize: filters?.pageSize,
          pageNum: filters?.pageNum,
          sortOrder: filters?.sortOrder,
          sortBy:
            // eslint-disable-next-line no-restricted-globals
            filters?.sortBy != null && !isNaN(Number(filters?.sortBy))
              ? Number(filters.sortBy)
              : filters?.sortBy,
          search: filters?.search,
        });
      }
      sendGetProspectListCountRequest({
        viewType: ViewType.Kanban,
        kanbanViewId: selectedKanbanCategoryId,
        search: filters?.search,
        ...(activeViewId ? { viewId: activeViewId } : {}),
      });

      sendGetProspectListCountRequest({
        viewType: ViewType.List,
        ...(activeViewId ? { viewId: activeViewId } : {}),
      });
    }
    setProspectViewIdFilter?.(null);
    closeProspectDetails();
    onHide();
  };

  const viewTagsModal = () => {
    setShowTagsModal(true);
  };

  const addToSequenceRequest = (sequenceId, stepId) => {
    hasResource(ResourceIdentifiers.SEQUENCES_CONTACTS_IMPORT) &&
      sendAddContactToStepRequest({
        sequenceId,
        stepId,
        contactIds: [selectedProspectId],
      });
  };

  const unsubscribeProspectRequest = () => {
    sendUnsubscribeProspectRequest([selectedProspectId]);
  };

  const removeProspect = () => {
    showDeleteProspectModal();
  };

  const removeProspectRequest = () => {
    sendRemoveProspectRequest([selectedProspectId]);
  };

  const sendGetSingleProspectSequencesRequest = () => {
    sendGetSingleProspectSequencesDetailsRequest(selectedProspectId);
  };

  const prospectSequenceMarkAsReplied = (stepId: number) => {
    const contactAndStepIds = [];
    contactAndStepIds.push({
      contactId: selectedProspectId,
      stepId,
    });
    sendSequenceMarkAsRepliedRequest(contactAndStepIds);
  };

  const prospectSequenceMarkedAsFinished = (stepId: number) => {
    const contactAndStepIds = [{ contactId: selectedProspectId, stepId }];
    sendSequenceMarkAsFinishedRequest(contactAndStepIds);
  };

  const prospectSequenceRemoveFromSequence = (sequenceId: number) => {
    sendSingleProspectRemoveFromSequencesRequest({
      sequenceId,
      contactIds: [selectedProspectId],
    });
  };

  useEffect(() => {
    if (selectedProspectId) {
      sendGetSingleProspectDetailsRequest(selectedProspectId);
    }
  }, [selectedProspectId]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getSingleProspectDetailsStatus,
      onFailed: () => {
        onHide();
      },
    });
  }, [getSingleProspectDetailsStatus]);

  useEffect(() => {
    if (!Array.isArray(prospects) || prospects?.length === 0) return;

    setAllListingProspects((prev) => {
      const mappedNew = prospects?.map((item) => ({
        id: item?.id,
        firstName: item?.firstName,
        lastName: item?.lastName,
      }));

      // prevent duplicates by id
      const existingIds = new Set(prev?.map((p) => p?.id));
      const uniqueNew = mappedNew.filter(
        (p) => p?.id && !existingIds?.has(p?.id),
      );
      return [...prev, ...uniqueNew];
    });
  }, [prospects]);

  // Sequence Prospect List
  useEffect(() => {
    if (!Array.isArray(sequenceProspects) || sequenceProspects?.length === 0) {
      return;
    }

    setAllListingProspects((prev) => {
      const mappedNew = sequenceProspects?.map((item) => ({
        id: item?.prospect?.id,
        firstName: item?.prospect?.firstName,
        lastName: item?.prospect?.lastName,
      }));

      // prevent duplicates by id
      const existingIds = new Set(prev?.map((p) => p?.id));
      const uniqueNew = mappedNew?.filter(
        (p) => p?.id && !existingIds?.has(p?.id),
      );
      return [...prev, ...uniqueNew];
    });
  }, [sequenceProspects]);
  // It is for to get ids for list view to change prospect using arrows
  useEffect(() => {
    if (callLogsVariant) return;
    if (!allListingProspects?.length) return;

    const selected = allListingProspects?.find(
      (item) => item?.id === selectedProspectId,
    );

    setProspectToNavigateFromModal({
      ids: allListingProspects?.map((p) => p?.id),
      columnName: `${selected?.firstName} ${selected?.lastName}`,
    });
  }, [allListingProspects, selectedProspectId, callLogsVariant, setProspectToNavigateFromModal]);

  // Add to Step Request.
  useEffect(() => {
    if (addToStepRequestStatus === RequestStatus.Succeeded) {
      hideAddToSequenceStepModal();
      resetSequenceAndStepData();
      sendGetSingleProspectSequencesRequest();
      // if (addToStepRequestSuccessfulCount > 0) {
      //   toaster.success(
      //     addToStepRequestMessage?.replace(/contact/gi, 'Prospect'),
      //   );
      // }
      // if (addToStepRequestFailedCount > 0) {
      //   toaster.error('Prospect already exists in the selected step', {
      //     theme: Theme.New,
      //   });
      // }
    }
    if (
      addToStepRequestStatus === RequestStatus.Failed &&
      addToStepRequestError
    ) {
      resetSequenceAndStepData();
      toaster.error(
        addToStepRequestError.message?.replace(/contact/gi, 'Prospect'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToStepRequestStatus]);

  // Unsubscribe Request.
  useEffect(() => {
    if (unsubscribeProspectStatus === RequestStatus.Succeeded) {
      toaster.success(
        unsubscribeProspectMessage?.replace(/contact/gi, 'Prospect'),
      );
      resetSequenceAndStepData();
    }
    if (
      unsubscribeProspectStatus === RequestStatus.Failed &&
      unsubscribeProspectError
    ) {
      toaster.error(
        unsubscribeProspectError.message?.replace(/contact/gi, 'Prospect'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsubscribeProspectStatus]);

  // remove Prospect Request.
  useEffect(() => {
    if (removeProspectStatus === RequestStatus.Succeeded) {
      onHide();
      toaster.success(removeProspectMessage?.replace(/contact/gi, 'Prospect'));
      resetSequenceAndStepData();
      resetSelectedProspectId();
    }
    if (removeProspectStatus === RequestStatus.Failed && removeProspectError) {
      toaster.error(
        removeProspectError.message?.replace(/contact/gi, 'Prospect'),
      );
      resetRemoveProspectRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeProspectStatus]);

  const handleSeqFetchingAfterAction = () => {
    setTimeout(() => {
      // Added delay to avoid race condition with the sequence list table for getting accurate backend data
      sendGetSingleProspectSequencesRequest();
      resetSequenceAndStepData();
    }, 300);
  };

  // Mark as replied for sequence.
  useEffect(() => {
    if (markAsRepliedStatus === RequestStatus.Succeeded) {
      toaster.success(markAsRepliedMessage?.replace(/contact/gi, 'Prospect'));
      handleSeqFetchingAfterAction();
    }
    if (markAsRepliedStatus === RequestStatus.Failed && markAsRepliedError) {
      toaster.error(
        markAsRepliedError.message?.replace(/contact/gi, 'Prospect'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAsRepliedStatus]);

  // Mark as replied for sequence.
  useEffect(() => {
    if (markAsFinishedStatus === RequestStatus.Succeeded) {
      toaster.success(markAsFinishedMessage?.replace(/contact/gi, 'Prospect'));
      handleSeqFetchingAfterAction();
    }
    if (markAsFinishedStatus === RequestStatus.Failed && markAsFinishedError) {
      toaster.error(
        markAsFinishedError.message?.replace(/contact/gi, 'Prospect'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAsFinishedStatus]);

  // Remove From sequence.
  useEffect(() => {
    if (removeFromSequenceStatus === RequestStatus.Succeeded) {
      toaster.success(
        removeFromSequenceMessage?.replace(/contact/gi, 'Prospect'),
      );
      handleSeqFetchingAfterAction();
    }
    if (
      removeFromSequenceStatus === RequestStatus.Failed &&
      removeFromSequenceError
    ) {
      toaster.error(
        removeFromSequenceError.message?.replace(/contact/gi, 'Prospect'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeFromSequenceStatus]);

  // Save Contact Attribute Request.
  useEffect(() => {
    if (saveContactAttributesRequestStatus === RequestStatus.Succeeded) {
      toaster.success(
        saveContactAttributesRequestMessage?.replace(/contact/gi, 'Prospect'),
      );
      resetSequenceAndStepData();
    }
    if (
      saveContactAttributesRequestStatus === RequestStatus.Failed &&
      saveContactAttributesRequestError
    ) {
      toaster.error(
        saveContactAttributesRequestError.message?.replace(
          /contact/gi,
          'Prospect',
        ),
      );
    }
    resetSaveContactAttributesRequestStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveContactAttributesRequestStatus]);

  const isRemoveProspectRequestPending =
    removeProspectStatus === RequestStatus.Pending;

  const contentRemoveProspectModal =
    'This will remove all the history of the prospect(s).';

  const titleRemoveProspectModal = 'Delete prospect(s)?';

  return (
    <>
      {show && (
        <ProspectDetailsModal
          show={show}
          onHide={handleHide}
          selectedProspectId={selectedProspectId}
          viewAddToSequenceModal={handleAddToSequenceStepModal}
          unsubscribeProspectRequest={unsubscribeProspectRequest}
          removeProspectRequest={removeProspect}
          selectedProspectData={selectedProspectData}
          sendGetSingleProspectSequencesDetailsRequest={
            sendGetSingleProspectSequencesRequest
          }
          getSingleProspectSequencesDetailsStatus={
            getSingleProspectSequencesDetailsStatus
          }
          selectedProspectSequences={selectedProspectSequences}
          prospectSequenceMarkAsReplied={prospectSequenceMarkAsReplied}
          prospectSequenceMarkAsFinished={prospectSequenceMarkedAsFinished}
          prospectSequenceRemoveFromSequence={
            prospectSequenceRemoveFromSequence
          }
          prospectSequenceAddToStep={addToSequenceRequest}
          sendSaveContactAttributesRequest={sendSaveContactAttributesRequest}
          sendGetSingleProspectDetailsRequest={
            sendGetSingleProspectDetailsRequest
          }
          getSingleProspectDetailsStatus={getSingleProspectDetailsStatus}
          saveContactAttributesRequestStatus={
            saveContactAttributesRequestStatus
          }
          isSingleProspectSubscribed={selectedProspectData?.isSubscribed}
          viewTagsModal={viewTagsModal}
          sendPrimaryEmailChangeInProspectRequest={
            sendPrimaryEmailChangeInProspectRequest
          }
          sendPrimaryPhoneChangeInProspectRequest={
            sendPrimaryPhoneChangeInProspectRequest
          }
          sendDeleteEmailFromProspectRequest={
            sendDeleteEmailFromProspectRequest
          }
          sendDeletePhoneFromProspectRequest={
            sendDeletePhoneFromProspectRequest
          }
          deleteSecondaryEmailRequestStatus={deleteSecondaryEmailRequestStatus}
          resetProspectDeleteEmailRequestStatus={
            resetProspectDeleteEmailRequestStatus
          }
          deleteSecondaryPhoneRequestStatus={deleteSecondaryPhoneRequestStatus}
          resetProspectDeletePhoneRequestStatus={
            resetProspectDeletePhoneRequestStatus
          }
          primaryEmailChangeRequestStatus={primaryEmailChangeRequestStatus}
          primaryPhoneChangeRequestStatus={primaryPhoneChangeRequestStatus}
          resetProspectPrimaryEmailChangeRequestStatus={
            resetProspectPrimaryEmailChangeRequestStatus
          }
          resetUpdatePrimaryNumberState={resetUpdatePrimaryNumberState}
          allOutcomes={allOutcomes}
          sendGetTagsRequest={sendGetTagsRequest}
          sendAddTagsToProspectsRequest={sendAddTagsToProspectsRequest}
          resetTags={resetTags}
          tags={tags}
          addTagsToProspectsRequestStatus={addTagsToProspectsRequestStatus}
          fields={fields}
          defaultTab={defaultTab}
          sendGetTasksRequest={sendGetTasksRequest}
          getTasksRequestStatus={getTasksRequestStatus}
          tasks={tasks}
          sendUpdateTasksRequest={sendUpdateTasksRequest}
          createTaskRequestStatus={createTaskRequestStatus}
          createTaskRequestMessage={createTaskRequestMessage}
          createTaskRequestError={createTaskRequestError}
          resetCreateTaskRequest={resetCreateTaskRequest}
          taskActionRequestStatus={taskActionRequestStatus}
          taskActionRequestMessage={taskActionRequestMessage}
          sendGetUndoTasksRequest={sendGetUndoTasksRequest}
          getUndoTasksRequestStatus={getUndoTasksRequestStatus}
          sendGetTaskAssigneeListRequest={sendGetTaskAssigneeListRequest}
          sendGetShareableTaskerDataRequest={sendGetShareableTaskerDataRequest}
          getShareableTaskerDataRequestStatus={
            getShareableTaskerDataRequestStatus
          }
          shareableTaskerData={shareableTaskerData}
          resetShareableTaskerState={resetShareableTaskerState}
          sendGetWhatsappLinkRequest={sendGetWhatsappLinkRequest}
          getWhatsappLinkRequestStatus={getWhatsappLinkRequestStatus}
          getWhatsappLinkResponse={getWhatsappLinkResponse}
          resetGetWhatsappLinkState={resetGetWhatsappLinkState}
          setShowGlobalRestrictionModal={setShowGlobalRestrictionModal}
          sendCreateTaskRequest={sendCreateTaskRequest}
          taskAssigneeList={taskAssigneeList}
          sendUploadAttachmentRequest={sendUploadAttachmentRequest}
          uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
          uploadAttachmentRequestError={uploadAttachmentRequestError}
          uploadedAttachment={uploadedAttachment}
          sendDeleteAttachmentRequest={sendDeleteAttachmentRequest}
          deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
          deleteAttachmentRequestError={deleteAttachmentRequestError}
          deletedAttachmentId={deletedAttachmentId}
          sendImageUploadRequest={sendImageUploadRequest}
          resetUploadAttachmentRequest={resetUploadAttachmentRequest}
          resetDeleteAttachmentRequest={resetDeleteAttachmentRequest}
          sendCreateProspectNoteRequest={sendCreateProspectNoteRequest}
          createProspectNoteRequestStatus={createProspectNoteRequestStatus}
          createProspectNoteRequestMessage={createProspectNoteRequestMessage}
          createProspectNoteResponse={createProspectNoteResponse}
          resetCreateProspectNotesListRequest={
            resetCreateProspectNotesListRequest
          }
          sendUpdateProspectNoteRequest={sendUpdateProspectNoteRequest}
          updateProspectNoteRequestStatus={updateProspectNoteRequestStatus}
          resetUpdateProspectNoteRequest={resetUpdateProspectNoteRequest}
          getFieldsRequest={getFieldsRequest}
          getSinglePropsectDetailPinnedFieldsRequest={
            getSinglePropsectDetailPinnedFieldsRequest
          }
          updateProspectPinStatusRequest={updateProspectPinStatusRequest}
          sendUpdateProspectFieldByIdRequest={
            sendUpdateProspectFieldByIdRequest
          }
          disabledModalNavArrows={disabledModalNavArrows}
          onModalNavArrowClick={handleModalNavArrowClick}
          showProspectNaviagateButtons={
            !callLogsVariant && prospectToNavigateFromModal?.ids?.length > 0
          }
          getSingleProspect={getSingleProspect}
          resetUpdateProspectFieldByIdRequest={
            resetUpdateProspectFieldByIdRequest
          }
          getSequenceListRequest={getSequenceListRequest}
          getStepListRequest={({ sequenceId }) => {
            getStepListRequest(sequenceId);
          }}
          addToStepRequestStatus={addToStepRequestStatus}
          addToStepRequestMessage={addToStepRequestMessage}
          addToStepRequestSuccessfulCount={addToStepRequestSuccessfulCount}
          addToStepRequestFailedCount={addToStepRequestFailedCount}
          sendRemoveTagsFromProspectsRequest={
            sendRemoveTagsFromProspectsRequest
          }
          removeTagsFromProspectsRequestStatus={
            removeTagsFromProspectsRequestStatus
          }
          sendAddTagsToProspectsRequestStatus={
            sendAddTagsToProspectsRequestStatus
          }
          sendRemoveTagsFromProspectsRequestStatus={
            sendRemoveTagsFromProspectsRequestStatus
          }
          sendGetProspectActivityCountRequest={
            sendGetProspectActivityCountRequest
          }
          getProspectActivityCountRequestStatus={
            getProspectActivityCountRequestStatus
          }
          editProspectAcitivityCount={editProspectAcitivityCount}
          isDemoAccount={isDemoAccount}
          callLogsVariant={callLogsVariant}
          callLogRow={callLogRow}
          callLogsProfileHeader={callLogsProfileHeader}
          prospectDetail={prospectDetail}
        />
      )}

      {showAddToSequenceModal && (
        <AddToSequenceModal
          show={showAddToSequenceModal}
          onHide={hideAddToSequenceStepModal}
          addToSequence={addToSequenceRequest}
          isSubscribed={selectedProspectData?.isSubscribed}
          currentSequenceId={currentSequenceId}
        />
      )}

      {showDeleteProspect && (
        // <ConfirmationModal
        //   show={showDeleteProspect}
        //   content={contentRemoveProspectModal}
        //   title={titleRemoveProspectModal}
        //   onClose={hideDeleteProspectModal}
        //   onSubmit={removeProspectRequest}
        //   isRequestPending={isRemoveProspectRequestPending}
        //   showSuccessButton={true}
        //   showCancelButton={true}
        //   cancelButtonText='Cancel'
        //   className='delete-prospect-modal'
        //   iconType={ConfirmationModalIconType.TRASH_RED}
        // />
        <ConfirmationModal
          show={showDeleteProspect}
          onClose={hideDeleteProspectModal}
          customClassName="prospect-delete-confirmation-modal"
          CustomHeaderIcon={
            <Trash width={24} height={24} className="trash-icon red-txt-16" />
          }
          customHeaderText={titleRemoveProspectModal}
          CustomContent={contentRemoveProspectModal}
          onSubmit={removeProspectRequest}
          hideHeader
        />
      )}
      {showTagsModal && (
        <TagsModal
          onHide={hideTagsModal}
          show={showTagsModal}
          selectedProspects={[selectedProspectData]}
        />
      )}
    </>
  );
};

export default ProspectDetails;
